import { useMemo, useState, type KeyboardEvent } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { CalendarDays, ChevronDown, Volume2 } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageProvider";
import { useDailyBrief } from "@/hooks/useDailyBrief";
import { usePremium } from "@/contexts/PremiumContext";
import { isPaymentUiBlocked } from "@/lib/platform";
import ReportContentButton from "@/components/report/ReportContentButton";
import { REPORT_SURFACES } from "@/lib/report/surfaces";
import { useReportedContent } from "@/contexts/ReportedContentContext";

/**
 * Daily Brief V1 — Calendar FASE 2 sub-bloco 3a.
 *
 * Morning Mirror context card rendered between the Home header and the
 * Hero. Pulls from `useDailyBrief()` which invokes the
 * `daily-brief-generate` edge fn (idempotent per user per local day).
 *
 * UX state:
 *   - loading: skeleton shimmer (keeps the card slot reserved so the
 *     hero below doesn't jump when the brief lands).
 *   - loaded + collapsed (default on mount): title + ~90-char preview +
 *     chevron down. Total ~70-80px tall. Mirror Hero stays the
 *     above-the-fold anchor.
 *   - loaded + expanded: title + full prose body + a static "Audio
 *     coming soon" label + View agenda button. Click anywhere outside
 *     the View agenda button toggles back to collapsed. Audio TTS
 *     deferred to V2/W5 (cron + cache + opt-in).
 *   - error: explain the unavailable brief and offer retry or Agenda.
 *   - empty: no personalized content to show yet.
 *
 * Expand state intentionally NOT persisted (no localStorage, no URL
 * state). Each Home mount resets to collapsed — V1 simple.
 */

const PREVIEW_MAX_CHARS = 90;

function buildPreview(content: string): string {
  if (content.length <= PREVIEW_MAX_CHARS) return content;
  return content.slice(0, PREVIEW_MAX_CHARS).trim() + "…";
}

// Map the user's local hour to the corresponding greeting i18n key.
// Three buckets matching what people actually say in PT/EN/NL:
//   05–11h → morning, 12–17h → afternoon, 18–04h → evening.
// Out-of-range hours fall back to morning (defensive — shouldn't happen).
function greetingKeyForHour(hour: number): string {
  if (hour >= 5 && hour < 12) return "dailyBrief.greeting.morning";
  if (hour >= 12 && hour < 18) return "dailyBrief.greeting.afternoon";
  return "dailyBrief.greeting.evening";
}

export function DailyBriefCard() {
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const { brief, loading, error, refresh } = useDailyBrief();
  const { isPremium } = usePremium();
  const { isReported } = useReportedContent();
  const [isExpanded, setIsExpanded] = useState(false);
  const [retrying, setRetrying] = useState(false);
  // V4.3: Free gets the template brief; frame it lightly as a Premium upsell
  // (hidden on iOS native per Apple 3.1.1).
  const showFreeFraming = !isPremium && !isPaymentUiBlocked();

  // useMemo must live here — above all early returns — to satisfy Rules of
  // Hooks (hooks must be called in the same order on every render).
  // Safe when brief is null: the guard inside returns "" immediately.
  const renderedContent = useMemo(() => {
    if (!brief?.content) return "";
    const hour = new Date().getHours();
    const greetingKey = greetingKeyForHour(hour);
    return brief.content.replace("{{GREETING}}", t(greetingKey));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brief?.content, lang]);

  // Corte pos-denuncia: o brief denunciado nao volta na Home no dia seguinte
  // ao reload — a Home simplesmente nao mostra o card. O brief do dia SEGUINTE
  // e outra linha, com outro id, e aparece normalmente: a denuncia e daquele
  // conteudo, nao do recurso.
  if (brief && isReported(REPORT_SURFACES.DAILY_BRIEF, brief.id)) {
    return null;
  }

  if (error) {
    const retry = async () => {
      if (retrying) return;
      setRetrying(true);
      try { await refresh(); }
      catch (err) { console.error("[DailyBriefCard] refresh failed", err); }
      finally { setRetrying(false); }
    };
    return (
      <section aria-labelledby="daily-brief-error-title" className="mb-5 rounded-3xl border border-border/60 bg-card p-5">
        <h2 id="daily-brief-error-title" className="text-sm font-semibold text-foreground">{t("dailyBrief.title")}</h2>
        <p role="status" className="mt-2 text-sm leading-relaxed text-muted-foreground">{t("ux.brief.unavailable")}</p>
        <div className="mt-4 flex flex-wrap gap-2">
          <button type="button" onClick={retry} disabled={retrying} className="min-h-11 rounded-xl bg-primary/10 px-4 text-sm font-medium text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-60">
            {retrying ? t("ux.loading") : t("common.retry")}
          </button>
          <button type="button" onClick={() => navigate("/agenda")} className="min-h-11 rounded-xl border border-border/60 px-4 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary">
            {t("dailyBrief.viewAgenda")}
          </button>
        </div>
      </section>
    );
  }
  if (!loading && !brief) return null;

  const hasContent = !loading && !!brief;
  const preview = brief ? buildPreview(renderedContent) : "";

  const handleToggle = () => {
    if (!hasContent) return;
    setIsExpanded((prev) => !prev);
  };

  const handleCardKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (!hasContent) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setIsExpanded((prev) => !prev);
    }
  };

  return (
    <motion.section
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
      className="relative w-full rounded-3xl overflow-hidden mb-5 group"
      style={{
        background:
          "linear-gradient(135deg, hsl(220 38% 8%) 0%, hsl(220 35% 6%) 100%)",
        border: "1px solid hsl(187 72% 50% / 0.22)",
        boxShadow:
          "0 0 24px hsl(187 72% 50% / 0.08), inset 0 1px 0 hsl(187 72% 50% / 0.10)",
      }}
    >
      <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-primary/25 to-transparent" />

      {/* Click region — the whole card toggles when collapsed/expanded.
          Children that need their own onClick (View agenda)
          stopPropagation locally so they don't fire the toggle. role
          + tabIndex + keyboard handler make it accessible despite not
          being a real <button> (a real button would forbid the nested
          interactive children). */}
      <motion.div
        layout
        transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
        role={hasContent ? "button" : undefined}
        tabIndex={hasContent ? 0 : undefined}
        aria-expanded={hasContent ? isExpanded : undefined}
        onClick={handleToggle}
        onKeyDown={handleCardKeyDown}
        className={
          "px-5 pt-4 pb-4 cursor-pointer " +
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-inset"
        }
      >
        <div className="flex items-center justify-between mb-2.5">
          <p
            className="text-[10px] font-display tracking-[0.22em] uppercase"
            style={{ color: "hsl(187 72% 50% / 0.65)" }}
          >
            {t("dailyBrief.title")}
          </p>
          {hasContent && (
            <motion.div
              animate={{ rotate: isExpanded ? 180 : 0 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
              className="text-muted-foreground/60 group-hover:text-primary/80 transition-colors"
            >
              <ChevronDown size={16} />
            </motion.div>
          )}
        </div>

        {loading ? (
          // Skeleton — three lines of varying length so it doesn't read
          // as a single missing bar.
          <div className="space-y-2.5">
            <div className="h-3 rounded bg-foreground/[0.08] animate-pulse" />
            <div className="h-3 rounded bg-foreground/[0.08] animate-pulse w-[92%]" />
            <div className="h-3 rounded bg-foreground/[0.08] animate-pulse w-[78%]" />
            <div className="h-3 rounded bg-foreground/[0.08] animate-pulse w-[55%]" />
          </div>
        ) : (
          <p
            className="font-display text-[14px] leading-[1.65] text-foreground/85 whitespace-pre-wrap"
          >
            {isExpanded ? renderedContent : preview}
          </p>
        )}

        <AnimatePresence initial={false}>
          {hasContent && isExpanded && (
            <motion.div
              key="actions"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
              className="overflow-hidden"
              // Action row absorbs clicks — taps on the View agenda
              // button must not bubble up and toggle collapse. The
              // audio-coming-soon label is a static <span> (no role,
              // no click) so it doesn't need its own handler.
              onClick={(e) => e.stopPropagation()}
            >
              {showFreeFraming && (
                <button
                  type="button"
                  onClick={() => navigate("/pricing")}
                  className="w-full text-left mt-3 text-[11px] font-display text-primary/70 hover:text-primary transition-colors"
                >
                  {t("dailyBrief.freeFraming")}
                </button>
              )}
              <div className="flex items-center justify-between mt-4 pt-3 border-t border-border/15">
                {/* Audio is V2/W5 — until cron + cache + opt-in TTS ship,
                    surface a static grey label instead of a disabled
                    button + non-responsive tooltip (both read as broken
                    UX to real users). One key, no interactive affordance. */}
                <div className="flex items-center gap-1">
                  <span
                    className="flex items-center gap-1.5 text-[11px] font-display text-muted-foreground/70"
                    aria-hidden="true"
                  >
                    <Volume2 size={13} className="opacity-60" />
                    {t("dailyBrief.audioComingSoon")}
                  </span>
                  {/* Denuncia — na linha de acoes do card expandido, ao lado do
                      rotulo de audio. O texto do brief e prosa do Claude sobre
                      a agenda do usuario: superficie de IA como qualquer outra,
                      mesmo nao sendo conversa. */}
                  {brief && (
                    <ReportContentButton
                      surface={REPORT_SURFACES.DAILY_BRIEF}
                      contentId={brief.id}
                      snapshot={renderedContent || brief.content}
                      context={{ brief_date: brief.brief_date, language: brief.language }}
                    />
                  )}
                </div>

                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate("/agenda");
                  }}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl transition-colors hover:bg-primary/[0.10]"
                  style={{
                    background: "hsl(187 72% 50% / 0.08)",
                    border: "1px solid hsl(187 72% 50% / 0.25)",
                  }}
                >
                  <CalendarDays size={13} style={{ color: "hsl(187 72% 60%)" }} />
                  <span
                    className="text-[11px] font-display font-medium"
                    style={{ color: "hsl(187 72% 60%)" }}
                  >
                    {t("dailyBrief.viewAgenda")}
                  </span>
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </motion.section>
  );
}

export default DailyBriefCard;
